import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"
import type { TrialCancellationResponse } from "@/hooks/useTeamSubscription"

interface TeamsQueryParams {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: "name" | "created_at" | "memberCount"
  sortOrder?: "asc" | "desc"
}

export interface Team {
  public_id: string
  name: string
  created_at: string
  memberCount: number
  gameCount: number
  clipCount: number
}

interface TeamsResponse {
  teams: Team[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export function useTeams(params: TeamsQueryParams = {}) {
  const { page = 1, pageSize = 20, search = "", sortBy = "created_at", sortOrder = "desc" } = params

  return useQuery<TeamsResponse>({
    queryKey: ["teams", page, pageSize, search, sortBy, sortOrder],
    queryFn: async () => {
      const response = await client.admin.teams.$get({
        query: {
          page,
          pageSize,
          search: search || undefined,
          sortBy,
          sortOrder,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch teams")
      }

      return response.json()
    },
    // Keep the current rows on screen while a new search/page loads, so the
    // table (and the search box's focus) isn't swapped for a skeleton per keystroke.
    placeholderData: keepPreviousData,
  })
}

export type CreateTeamPayload = {
  name: string
  creatorEmail?: string
  creatorUserPublicId?: string
  organizationPublicId?: string
  league?: string
  level?: string
  type?: "football"
  /**
   * Comp a plan at creation: a real Stripe subscription at 100% off, same as
   * Comp Plan on the team page. `expiresAt` is an explicit null for "no end
   * date" — the backend reads the key's presence, and JSON drops undefined.
   * Mutually exclusive with `startTrial`.
   */
  comp?: { planId: string; reason: string; expiresAt: string | null }
  startTrial?: boolean
  /**
   * The coach whose diagrams welcome-offer request this create provisions, when it comes from the
   * Offers page (`/offers/diagrams/:publicId`). The grant terms (Team plan, one year) are derived
   * server-side, so `comp` should not be sent with it.
   */
  welcomeOfferUserPublicId?: string
}

export type CreateTeamCompResult =
  | {
      kind: "granted"
      planId: string
      stripeSubscriptionId: string
      expiresAt: string | null
      accessResolution: string
    }
  /** The team exists; the comp did not happen. Finish with Comp Plan on the team page. */
  | { kind: "failed"; planId: string; message: string }

export type AdminTrialStartResult =
  | { kind: "orphan_activated" }
  | { kind: "trial_created" }
  | { kind: "organization_inherited" }
  | { kind: "skipped_existing_active" }
  | { kind: "trial_failed" }

export type CreateTeamResponse = {
  ok: true
  team: { public_id: string; name: string }
  creator: { public_id: string; email: string }
  organization: { public_id: string; name: string } | null
  trialCancellation: TrialCancellationResponse
  comp: CreateTeamCompResult | null
  organizationAccess?: boolean
  trialStarted: AdminTrialStartResult | null
  created: true
}

export function useCreateTeam() {
  const qc = useQueryClient()
  return useMutation<CreateTeamResponse, APIError, CreateTeamPayload>({
    mutationFn: async (payload) => {
      const res = await (client.admin.teams as any).$post({ json: payload })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "TEAM_CREATE_FAILED",
          new Error(err.message || "Failed to create team")
        )
      }
      return (await res.json()) as CreateTeamResponse
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["teams"] })
      qc.invalidateQueries({ queryKey: ["admin-organizations"] })
      if (variables.organizationPublicId) {
        qc.invalidateQueries({
          queryKey: ["admin-organization", variables.organizationPublicId],
        })
      }
    },
  })
}
